import React, { useState, useEffect, useRef } from "react";
import { Modal, Select, Radio, Button, Collapse, Switch, Tooltip } from "antd";
import Draggable from 'react-draggable';
import PlusMinQty from "../../utilities/PlusMinQty";
import { calculatePrice } from "../../../util/helper";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const { Option } = Select;

const OrderWindowModal = ({
  show,
  onHide,
  orderData,
  onExecute,
  onAddToBasket,
  onSave,
  isEditMode = false,
  lotSize = 1,
  sockSource,
  slicingData
}) => {
  const [orders, setOrders] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const ticksRef = useRef({});
  const [disabled, setDisabled] = useState(true);
  const draggleRef = useRef(null);
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });

  
  useEffect(() => {
    if (show && orderData?.orders) {
      const sortedInitialOrders = [...orderData.orders].sort((a, b) => {
        if (a.side === "BUY" && b.side === "SELL") return -1;
        if (a.side === "SELL" && b.side === "BUY") return 1;
        return 0;
      });
      const sanitizedOrders = sortedInitialOrders.map(order => {
        if ((order.orderType === "MARKET" || order.orderType === "Mkt protect") && order.marketProtect > 0) {
          return { ...order, orderType: "Mkt protect" };
        }
        return order;
      });
      setOrders(sanitizedOrders);
      setSelectedIndex(0);
      const initialTicks = {};
      orderData.orders.forEach(o => {
        if (o.token) {
          initialTicks[o.token] = { lp: o.triggerPrice || 0 };
        }
      });
      ticksRef.current = initialTicks;
    }
  }, [show, orderData]);
  
  useEffect(() => {
    if (!show || !sockSource || orders.length === 0) return;

    const tokens = Array.from(new Set(orders.map(o => o.token))).filter(Boolean);
    const subscribers = tokens.map(token => {
      const sub = sockSource.subscribe("" + token, (message) => {
        const payload = message?.data?.data || message?.data || message;
        if (payload && payload.lp !== undefined) {
          ticksRef.current[token] = payload;
          const ltpElem = document.getElementById("order-modal-ltp");
          if (ltpElem && token === orders[selectedIndex]?.token) {
            ltpElem.innerText = payload.lp.toFixed(2);
          }
        }
      });
      return sub;
    });
    
    return () => {
      subscribers.forEach(sub => sub.unsubscribe());
    };
  }, [show, sockSource, orders, selectedIndex]);
  
  const currentOrder = orders[selectedIndex] || {};
  
  const onStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) return;
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };

  const handleFieldChange = (field, value) => {
    const updatedOrders = [...orders];
    let item = { ...updatedOrders[selectedIndex], [field]: value };

    // Auto-switch between MARKET and Mkt protect based on marketProtect percentage
    if (field === "marketProtect" || field === "orderType") {
      if ((item.orderType === "MARKET" || item.orderType === "Mkt protect")) {
        if (item.marketProtect > 0) {
          item.orderType = "Mkt protect";
        } else {
          item.orderType = "MARKET";
        }
      }
    }

    if (item.orderType === "SL-M" && (field === "triggerPrice" || field === "marketProtect" || field === "orderType" || field === "side")) {
      const mProtect = item.marketProtect;
      const calculated = calculatePrice(item.triggerPrice, mProtect, item.side);
      item.price = calculated.price;
    }
    updatedOrders[selectedIndex] = item;
    setOrders(updatedOrders);
  };

  const handleOrderTypeChange = (e) => {
    handleFieldChange("orderType", e.target.value);
  };

  const isPriceEnabled = () => {
    return currentOrder.orderType === "LIMIT" || currentOrder.orderType === "SL" || currentOrder.orderType === "Limit at LTP" || currentOrder.orderType === "Limit with buffer";
  };

  const isTriggerPriceEnabled = () => {
    return currentOrder.orderType === "SL-M" || currentOrder.orderType === "SL";
  };

  const getProcessedOrders = () => {
    return orders
      .map((order) => {
        let orderType = order.orderType;
        if (orderType === "Limit at LTP" || orderType === "Limit with buffer") 
          orderType = "LIMIT";
        
        // Final safety check for Mkt protect
        if (orderType === "MARKET" && order.marketProtect > 0) {
          orderType = "Mkt protect";
        }

        if (orderType === "Mkt protect" || orderType === "LIMIT" || orderType === "MARKET") {
          return { ...order, orderType, triggerPrice: null };
        }
        return { ...order, orderType };
      });
  };

  const handleExecute = () => {
    onExecute({ orders: getProcessedOrders() });
    onHide();
  };

  const handleAddToBasket = () => {
    onAddToBasket({ orders: getProcessedOrders() });
    onHide();
  };

  const handleSave = () => {
    onSave({ orders: getProcessedOrders() });
    onHide();
  };

  const sideColor = currentOrder.side === "BUY" ? "#04b488" : "#ed5533";
  const currentLTP = ticksRef.current[currentOrder.token]?.lp || 0;
  const isTriggerInvalid = currentOrder.orderType === "SL" && (
    currentOrder.side === "BUY" 
      ? currentOrder.triggerPrice > currentOrder.price 
      : currentOrder.triggerPrice < currentOrder.price
  );

  return (
    <Modal
      open={show}
      onCancel={onHide}
      width={530}
      className="order-window-modal"
      modalRender={(modal) => (
        <Draggable
          disabled={disabled}
          bounds={bounds}
          nodeRef={draggleRef}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
      title={
        <div
          className="order-window-title"
          onMouseOver={() => {
            if (disabled) setDisabled(false);
          }}
          onMouseOut={() => {
            setDisabled(true);
          }}
        >
          <div className="order-window-header-row">
            <span style={{ color: sideColor, fontWeight: 700 }}>{currentOrder.side}</span>
            <span className="display-name">{currentOrder.displayName}</span>
            {slicingData && (
              <Tooltip overlayStyle={{ position: 'fixed' }} title={
                <div style={{ padding: '4px' }}>
                  <p style={{ marginBottom: '8px', fontWeight: 600 }}>Order Slicing Details:</p>
                  <p style={{ marginBottom: '4px', fontSize: '12px' }}>Total Orders: <strong>{slicingData.totalOrders}</strong></p>
                  <p style={{ marginBottom: '4px', fontSize: '12px' }}>Max Qty/Order: <strong>{slicingData.maxQty} ({slicingData.maxLot} lots)</strong></p>
                  <p style={{ marginBottom: '4px', fontSize: '12px' }}>Total Qty: <strong>{slicingData.totalOrderQty} ({slicingData.totalOrderLot} lots)</strong></p>
                  <p style={{ marginBottom: '0', fontSize: '12px', borderTop: '1px solid #ffffff33', paddingTop: '4px', marginTop: '4px', opacity: 0.9 }}>
                    <strong>Note:</strong> Broker API limit is 10 orders/sec.
                  </p>
                </div>
              }>
                <WarningAmberIcon 
                  style={{ color: '#faad14', marginLeft: '8px', fontSize: '18px' }} 
                />
              </Tooltip>
            )}
          </div>
          <div className="order-window-ltp-container">
            LTP: <span id="order-modal-ltp" className="ltp-value order-window-ltp-value">{currentLTP.toFixed(2)}</span>
          </div>
        </div>
      }
      footer={
        isEditMode ? [
          <Button key="cancel" onClick={onHide}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>
        ] : [
        <Button key="cancel" onClick={onHide}>
          Cancel
        </Button>,
        <Button key="basket" type="default" onClick={handleAddToBasket} disabled={isTriggerInvalid}>
          Add to Basket
        </Button>,
        <Button
          key="execute"
          type="primary"
          onClick={handleExecute}
          style={orders.length > 1 ? {} : {backgroundColor: sideColor, borderColor: sideColor }}
          disabled={isTriggerInvalid}
        >
          {orders.length > 1 ? "Execute Basket" : `Execute ${orders[0]?.side || currentOrder.side}`}
        </Button>,
      ]}
    >
      <div className="order-window--body">
        <div className="order-window-top-row">
          <Radio.Group
            value={currentOrder.product}
            onChange={(e) => handleFieldChange("product", e.target.value)}
            buttonStyle="solid"
            size="middle"
          >
            <Radio style={{ fontWeight: 400 }} value="NRML">NRML</Radio>
            <Radio style={{ fontWeight: 400 }} value="MIS">MIS</Radio>
          </Radio.Group>

          <div className="order-window-side-switch">
          <span className="order-window-side-label">{currentOrder.side}</span>
            <Switch
              checked={currentOrder.side === "BUY"}
              onChange={(checked) => handleFieldChange("side", checked ? "BUY" : "SELL")}
              style={{ backgroundColor: currentOrder.side === "BUY" ? "#04b488" : "#ed5533" }}
            />
          </div>
        </div>

        <div className="order-window-input-group">
          <div>
            <div className="field-label order-window-field-label">
              Qty (Lots) <span style={{ opacity: 0.7 }}>[{currentOrder.quantity * (currentOrder.lotSize || lotSize || 1)}]</span>
            </div>
            <PlusMinQty
              filterValue={currentOrder.quantity}
              qtyClick={(value) => handleFieldChange("quantity", value || 1)}
              min={1}
              step={1}
            />
          </div>
          <div>
            <div className="field-label order-window-field-label">
              Price
            </div>
            <div style={{ position: 'relative' }}>
              <PlusMinQty
                filterValue={(isPriceEnabled() || currentOrder.orderType === "SL-M") ? currentOrder.price : ""}
                qtyClick={(value) => handleFieldChange("price", value || 0)}
                min={0}
                step={currentOrder.tickSize || 0.05}
                disabled={!isPriceEnabled()}
                style={{ 
                  opacity: isPriceEnabled() ? 1 : (currentOrder.orderType === "SL-M" ? 1 : 0.4), 
                  transition: 'opacity 0.2s' 
                }}
                formatINR={true}
              />
            </div>
          </div>
          <div>
            <div className="field-label order-window-field-label">Trigger Price</div>
            <PlusMinQty
              filterValue={isTriggerPriceEnabled() ? currentOrder.triggerPrice : ""}
              qtyClick={(value) => handleFieldChange("triggerPrice", value || 0)}
              min={0}
              step={currentOrder.tickSize || 0.05}
              disabled={!isTriggerPriceEnabled()}
              style={{ opacity: isTriggerPriceEnabled() ? 1 : 0.4, transition: 'opacity 0.2s' }}
              formatINR={true}
            />
          </div>
          <div>
            <div className="field-label order-window-field-label">Mkt Protect %</div>
            <PlusMinQty
              filterValue={(currentOrder.orderType === "MARKET" || currentOrder.orderType === "SL-M" || currentOrder.orderType === "Mkt protect") ? (currentOrder.marketProtect) : ""}
              qtyClick={(value) => handleFieldChange("marketProtect", value ?? 0)}
              min={0}
              step={1}
              disabled={!(currentOrder.orderType === "MARKET" || currentOrder.orderType === "SL-M" || currentOrder.orderType === "Mkt protect")}
              style={{ opacity: (currentOrder.orderType === "MARKET" || currentOrder.orderType === "SL-M" || currentOrder.orderType === "Mkt protect") ? 1 : 0.4, transition: 'opacity 0.2s' }}
            />
          </div>
        </div>

        {isTriggerInvalid && (
          <div className="order-window-error-msg">
            {currentOrder.side === "BUY" 
              ? "Trigger price cannot be greater than price" 
              : "Trigger price cannot be less than price"}
          </div>
        )}

        <div>
          <Radio.Group
            onChange={handleOrderTypeChange}
            value={(currentOrder.orderType === "MARKET" || currentOrder.orderType === "Mkt protect") ? "MARKET" : (currentOrder.orderType === "Limit at LTP"  || currentOrder.orderType === "Limit with buffer") ? "LIMIT" : currentOrder.orderType}
            style={{ width: '100%' }}
          >
            <div className="order-window-type-container">
              <Radio className="order-type-radio" style={{ fontWeight: 400 }} value="MARKET">
                {(currentOrder.orderType === "MARKET" || currentOrder.orderType === "Mkt protect") && currentOrder.marketProtect > 0 ? "Mkt protect" : "Market"}
              </Radio>
              <Radio className="order-type-radio" style={{ fontWeight: 400 }} value="LIMIT">Limit</Radio>
              <Radio className="order-type-radio" style={{ fontWeight: 400 }} value="SL-M">SL-M</Radio>
              <Radio className="order-type-radio" style={{ fontWeight: 400 }} value="SL">SL-L</Radio>
            </div>
          </Radio.Group>
        </div>

        {/* Summary Table */}
        <div>
          <Collapse
            ghost
            defaultActiveKey={['1']}
            items={[{
              key: '1',
              label: <span className="order-window-legs-label">Order Legs ({orders.length})</span>,
              children: (
                <div className="legs-container order-window-legs-container">
                  <table className="legs-table order-window-legs-table">
                    <thead>
                      <tr>
                        <th className="order-window-th">Instrument</th>
                        <th className="order-window-th center">Side</th>
                        <th className="order-window-th center">Type</th>
                        <th className="order-window-th right">Lots</th>
                        <th className="order-window-th center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order, index) => (
                        <tr
                          key={index}
                          className={selectedIndex === index ? "selected-row" : ""}
                          style={{
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedIndex(index)}
                        >
                          <td className="text-primary-themed" style={{ padding: '10px 12px', fontWeight: 500 }}>{order.displayName}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              color: order.side === "BUY" ? "#04b488" : "#ed5533",
                              fontWeight: 700,
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: order.side === "BUY" ? "#04b48815" : "#ed553315"
                            }}>
                              {order.side}
                            </span>
                          </td>
                          <td className="text-secondary-themed" style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {((order.orderType === "Mkt protect") && order.marketProtect > 0)
                              ? `Mkt protect(${order.marketProtect}%)` : (order.orderType === 'Mkt protect' && order.marketProtect === 0)? 'MARKET' :(order.orderType === "Limit at LTP" || order.orderType === "Limit with buffer") ? "LIMIT" : order.orderType}
                          </td>
                          <td className="text-primary-themed order-window-td-qty">{order.quantity}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <Button
                              type={selectedIndex === index ? "primary" : "text"}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedIndex(index);
                              }}
                              className="edit-btn-themed"
                              style={{ fontSize: '11px', height: '24px' }}
                            >
                              {selectedIndex === index ? 'Editing' : 'Edit'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            }]}
          />
        </div>
      </div>
    </Modal>
  );
};

export default OrderWindowModal;

